"use client";

import { DEFAULT_COLOR_OPTIONS } from "@octogen/catalog-filters";

import { AdvancedAttributesFilter } from "@/components/results/filters/AdvancedAttributesFilter";
import { BrandsFilter, type BrandOption } from "@/components/results/filters/BrandsFilter";
import { CategoryFilter } from "@/components/results/filters/CategoryFilter";
import { ColorFamilyFilter } from "@/components/results/filters/ColorFamilyFilter";
import { PriceFilter } from "@/components/results/filters/PriceFilter";
import { SimilarToBrandsFilter } from "@/components/results/filters/SimilarToBrandsFilter";
import type { CategoryNode } from "@/hooks/useResultsFilters";

import type { ShopperFilters } from "@/lib/shopper-search";

interface FiltersContentProps {
  // Category filter
  categories: CategoryNode[];
  navigationStack: Array<{ name: string; path: string }>;
  onNavigationChange: (stack: Array<{ name: string; path: string }>) => void;
  onCategoryNavigate: (path: string) => void;
  onCategoryRootNavigate?: () => void;
  isHierarchyNavigationEnabled?: boolean;
  currentCategoryPath?: string;
  selectedLeafPaths: string[];
  onLeafToggle: (path: string) => void;
  isLoadingTaxonomy: boolean;

  // Price filter
  priceMin: number;
  priceMax: number;
  priceRange: [number, number];
  onPriceChange: (range: [number, number]) => void;

  // Color filter
  selectedColors: string[];
  onColorChange: (colors: string[]) => void;
  colorCounts?: Map<string, number>;

  // Brands filter
  availableBrands: BrandOption[];
  selectedBrands: string[];
  onBrandChange: (brands: string[]) => void;
  isLoadingBrands: boolean;

  // Similar to brands filter
  selectedSimilarToBrands: string[];
  onSimilarToBrandChange: (brands: string[]) => void;

  // Visibility overrides
  showBrandsFilter?: boolean;
  showSimilarToBrandsFilter?: boolean;
  advancedFilterPosition?: "default" | "afterCategory";

  // Advanced attributes filter
  searchParams: ShopperFilters | null;
  selectedAdvancedAttributes: Map<string, Set<string>>;
  advancedAttributeValueCounts?: Map<string, Map<string, number>>;
  onAdvancedAttributeToggle: (attributeKey: string, value: string) => void;
}

/**
 * Shared filters content component used in both desktop sidebar and mobile sheet.
 * Contains all filter components: Category, Price, Color, Brands, Similar Brands, Advanced.
 */
export function FiltersContent({
  // Category
  categories,
  navigationStack,
  onNavigationChange,
  onCategoryNavigate,
  onCategoryRootNavigate,
  isHierarchyNavigationEnabled,
  currentCategoryPath,
  selectedLeafPaths,
  onLeafToggle,
  isLoadingTaxonomy,
  // Price
  priceMin,
  priceMax,
  priceRange,
  onPriceChange,
  // Color
  selectedColors,
  onColorChange,
  colorCounts,
  // Brands
  availableBrands,
  selectedBrands,
  onBrandChange,
  isLoadingBrands,
  // Similar to brands
  selectedSimilarToBrands,
  onSimilarToBrandChange,
  showBrandsFilter = true,
  showSimilarToBrandsFilter = true,
  advancedFilterPosition = "default",
  // Advanced
  searchParams,
  selectedAdvancedAttributes,
  advancedAttributeValueCounts,
  onAdvancedAttributeToggle,
}: FiltersContentProps) {
  const advancedAttributesFilter = (
    <AdvancedAttributesFilter
      searchParams={searchParams}
      selectedAttributes={selectedAdvancedAttributes}
      attributeValueCounts={advancedAttributeValueCounts}
      onToggle={onAdvancedAttributeToggle}
    />
  );

  return (
    <div className="flex w-full flex-col gap-0">
      {/* Category Filter - Drill-down navigation */}
      {/* Show when: has categories OR has navigation stack (for back button at leaf levels) */}
      {!isLoadingTaxonomy && (categories.length > 0 || navigationStack.length > 0) && (
        <CategoryFilter
          label="CATEGORY"
          categories={categories}
          navigationStack={navigationStack}
          onNavigationChange={onNavigationChange}
          onCategoryNavigate={onCategoryNavigate}
          onRootNavigate={onCategoryRootNavigate}
          isHierarchyNavigationEnabled={isHierarchyNavigationEnabled}
          currentPath={currentCategoryPath}
          selectedLeafPaths={selectedLeafPaths}
          onLeafToggle={onLeafToggle}
        />
      )}

      {advancedFilterPosition === "afterCategory" && advancedAttributesFilter}

      {/* Price Filter */}
      <PriceFilter min={priceMin} max={priceMax} value={priceRange} onChange={onPriceChange} />

      {/* Color Family Filter */}
      <ColorFamilyFilter
        colors={DEFAULT_COLOR_OPTIONS}
        selected={selectedColors}
        onChange={onColorChange}
        counts={colorCounts}
      />

      {/* Brands Filter */}
      {showBrandsFilter && (
        <BrandsFilter
          brands={availableBrands}
          selected={selectedBrands}
          onChange={onBrandChange}
          isLoading={isLoadingBrands}
        />
      )}

      {/* Similar To Brands Filter - Style-based similarity */}
      {showSimilarToBrandsFilter && (
        <SimilarToBrandsFilter
          brands={availableBrands}
          selected={selectedSimilarToBrands}
          onChange={onSimilarToBrandChange}
          isLoading={isLoadingBrands}
        />
      )}

      {/* Advanced Attributes Filter */}
      {advancedFilterPosition === "default" && advancedAttributesFilter}
    </div>
  );
}
